import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import BreakingNews from '../components/BreakingNews';
import Footer from '../components/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Download, Eye, FileText, QrCode } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SubscribePage = () => {
  const { toast } = useToast();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [showQR, setShowQR] = useState(false);
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    upi_transaction_id: '',
  });

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads') || url.startsWith('/uploads')) return `${API_URL}${url}`;
    return url;
  };

  const plans = [
    {
      id: 'monthly',
      name: 'Monthly',
      nameBengali: 'মাসিক',
      price: 99,
      duration: '1 Month',
      durationBengali: '১ মাস',
      features: [
        'Daily E-Paper Access',
        'Archive Access (30 days)',
        'PDF Downloads',
        'Ad-Free Reading'
      ],
      featuresBengali: [
        'দৈনিক ই-পেপার অ্যাক্সেস',
        'আর্কাইভ অ্যাক্সেস (৩০ দিন)',
        'পিডিএফ ডাউনলোড',
        'বিজ্ঞাপন মুক্ত পঠন'
      ]
    },
    {
      id: 'quarterly',
      name: 'Quarterly',
      nameBengali: 'ত্রৈমাসিক',
      price: 249,
      duration: '3 Months',
      durationBengali: '৩ মাস',
      popular: true,
      savings: 'Save ₹48',
      savingsBengali: '₹৪৮ সাশ্রয়',
      features: [
        'Daily E-Paper Access',
        'Archive Access (90 days)',
        'PDF Downloads',
        'Ad-Free Reading',
        'Priority Support'
      ],
      featuresBengali: [
        'দৈনিক ই-পেপার অ্যাক্সেস',
        'আর্কাইভ অ্যাক্সেস (৯০ দিন)',
        'পিডিএফ ডাউনলোড',
        'বিজ্ঞাপন মুক্ত পঠন',
        'অগ্রাধিকার সহায়তা'
      ]
    },
    {
      id: 'yearly',
      name: 'Yearly',
      nameBengali: 'বার্ষিক',
      price: 899,
      duration: '12 Months',
      durationBengali: '১২ মাস',
      savings: 'Save ₹289',
      savingsBengali: '₹২৮৯ সাশ্রয়',
      features: [
        'Daily E-Paper Access',
        'Unlimited Archive Access',
        'PDF Downloads',
        'Ad-Free Reading',
        'Priority Support',
        'Exclusive Newsletter'
      ],
      featuresBengali: [
        'দৈনিক ই-পেপার অ্যাক্সেস',
        'সীমাহীন আর্কাইভ অ্যাক্সেস',
        'পিডিএফ ডাউনলোড',
        'বিজ্ঞাপন মুক্ত পঠন',
        'অগ্রাধিকার সহায়তা',
        'এক্সক্লুসিভ নিউজলেটার'
      ]
    }
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const handleSubscribe = () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }
    setShowQR(true);
  };

  const handleConfirmPayment = async () => {
    if (!formData.upi_transaction_id) {
      toast({ title: 'Error', description: 'Please enter UPI transaction ID', variant: 'destructive' });
      return;
    }

    try {
      const plan = plans.find(p => p.id === selectedPlan);
      const response = await axios.post(`${API_URL}/api/subscribe`, {
        ...formData,
        plan: selectedPlan,
        amount: plan.price,
      });

      toast({
        title: 'Subscription Successful!',
        description: `Your subscription request has been received. Subscription ID: ${response.data.subscription_id}. You will receive confirmation email shortly after payment verification.`,
      });

      // Reset form
      setFormData({ name: '', email: '', phone: '', upi_transaction_id: '' });
      setShowQR(false);
      setSelectedPlan('monthly');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Subscription failed. Please try again.';
      toast({ title: 'Error', description: errorMsg, variant: 'destructive' });
    }
  };

  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <BreakingNews />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-red-600 to-red-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            ই-পেপার সাবস্ক্রিপশন
          </h1>
          <p className="text-xl md:text-2xl mb-2">E-Paper Subscription</p>
          <p className="text-red-100 max-w-2xl mx-auto">
            Get unlimited access to daily newspaper, archives, and exclusive content
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        {!showQR ? (
          <div className="grid gap-8">
            {/* Subscription Plans */}
            <div>
              <h2 className="text-3xl font-bold text-center mb-8">Choose Your Plan</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
                {plans.map((plan) => (
                  <Card
                    key={plan.id}
                    className={`relative cursor-pointer transition-all duration-200 hover:shadow-xl ${
                      selectedPlan === plan.id
                        ? 'border-2 border-red-600 shadow-lg'
                        : 'border border-gray-200'
                    } ${plan.popular ? 'transform md:-translate-y-4' : ''}`}
                    onClick={() => setSelectedPlan(plan.id)}
                  >
                    {plan.popular && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-yellow-500 text-gray-900 px-4 py-1">Most Popular</Badge>
                      </div>
                    )}
                    <CardHeader className="text-center">
                      <CardTitle className="text-2xl mb-2">{plan.nameBengali}</CardTitle>
                      <CardDescription className="text-lg">{plan.name}</CardDescription>
                      <div className="mt-4">
                        <span className="text-4xl font-bold">₹{plan.price}</span>
                        <p className="text-gray-600 mt-1">{plan.duration}</p>
                        {plan.savings && (
                          <Badge variant="outline" className="mt-2 border-green-600 text-green-600">
                            {plan.savingsBengali}
                          </Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3">
                        {plan.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm">{plan.featuresBengali[index]}</p>
                              <p className="text-xs text-gray-500">{feature}</p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* User Details Form */}
            <Card className="max-w-2xl mx-auto w-full">
              <CardHeader>
                <CardTitle>Your Details</CardTitle>
                <CardDescription>Fill in your information to continue</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <Label>Phone Number *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+91 XXXXXXXXXX"
                    required
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Selected Plan:</strong> {selectedPlanData?.nameBengali} ({selectedPlanData?.name}) - ₹{selectedPlanData?.price}
                  </p>
                </div>
                <Button
                  onClick={handleSubscribe}
                  className="w-full bg-red-600 hover:bg-red-700 text-lg py-6"
                >
                  Proceed to Payment →
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          /* Payment QR Code */
          <Card className="max-w-2xl mx-auto">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Complete Payment</CardTitle>
              <CardDescription>Scan QR code to pay via UPI</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-lg p-8 text-center">
                <div className="mb-4">
                  <Badge className="bg-green-600 text-lg px-4 py-2">
                    Amount: ₹{selectedPlanData?.price}
                  </Badge>
                </div>
                
                {settings?.upi_qr_code_url ? (
                  <div className="flex flex-col items-center">
                    <div className="bg-white p-6 rounded-lg shadow-lg inline-block">
                      <img
                        src={getImageUrl(settings.upi_qr_code_url)}
                        alt="UPI QR Code"
                        className="w-64 h-64 mx-auto"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'block'; }}
                      />
                      <div style={{ display: 'none' }} className="text-center text-gray-500 py-8">
                        <QrCode className="w-32 h-32 mx-auto text-gray-300 mb-4" />
                        <p>QR Code failed to load</p>
                      </div>
                    </div>
                    {settings.upi_id && (
                      <p className="mt-4 text-sm text-gray-600">
                        UPI ID: <span className="font-mono font-semibold">{settings.upi_id}</span>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-white p-12 rounded-lg">
                    <QrCode className="w-32 h-32 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500">QR Code not configured</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-semibold text-yellow-900 mb-2">📱 Payment Instructions:</p>
                  <ol className="text-xs text-yellow-800 space-y-1 list-decimal list-inside">
                    <li>Open any UPI app (PhonePe, PayTM, Google Pay)</li>
                    <li>Scan the QR code above</li>
                    <li>Pay ₹{selectedPlanData?.price}</li>
                    <li>Copy the transaction ID from your app</li>
                    <li>Enter the transaction ID below</li>
                  </ol>
                </div>

                <div>
                  <Label>UPI Transaction ID *</Label>
                  <Input
                    value={formData.upi_transaction_id}
                    onChange={(e) => setFormData({ ...formData, upi_transaction_id: e.target.value })}
                    placeholder="Enter 12-digit transaction ID"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Find this in your UPI app after payment
                  </p>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowQR(false)}
                    className="flex-1"
                  >
                    ← Back
                  </Button>
                  <Button
                    onClick={handleConfirmPayment}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    Confirm Payment ✓
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SubscribePage;
